import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

// "Akıllı istatistik" görsel imzası (Görev 12, spec §13.2). Mevcut StatCard
// (pages/dashboard/styles.js) marka gradyanını (#0291EF → #02D3BF) DOLGU olarak kullanıyor;
// burada AYNI gradyan ÇERÇEVE olarak kullanılıyor. Bu bilinçli bir karşıtlık: dolgu = düz
// istatistik, çerçeve = istatistiksel bir modelden gelen "akıllı" istatistik. İki bileşenin
// aynı görünüme yakınsamaması gerekiyor — o yüzden burada dolgu YOK, yalnız çerçeve var.
//
// Çerçeve gradyanı klasik padding-box/border-box tekniğiyle kuruluyor: birinci (üstteki)
// katman kart yüzeyini yalnızca padding-box'a kırpıyor, ikinci katman aynı gradyanı
// border-box'a (çerçeve dahil) uyguluyor — üstteki katman çerçeve halkasını KAPSAMADIĞI için
// halka ikinci katmandaki gradyanı gösteriyor.
//
// Dönen/parıldayan çerçeve --ha-angle adlı bir CSS custom property'sinin @property ile
// <angle> olarak kayıt edilmesine dayanıyor (yalnızca kayıtlı/tipli custom property'ler
// @keyframes ile pürüzsüz interpolasyon alır — Houdini Properties & Values API). Bu at-rule
// stylis tarafından @keyframes gibi otomatik olarak dosyanın en üstüne taşınır (doğrulandı),
// ayrı bir createGlobalStyle gerekmez. Desteklemeyen tarayıcılar @property bloğunu sessizce
// yok sayar; biz de JS tarafında aynı desteği (CSS.registerProperty varlığı üzerinden) ayrıca
// kontrol edip animasyonu hiç uygulamayarak statik gradyan çerçeveye düşüyoruz.

const SHIMMER_DURATION_MS = 1200; // "veri yüklendiğinde tek tur parıltı (~1,2 sn)"
const ROTATE_DURATION_S = 10; // "hover'da yavaş dönüş (10 sn/tur)"

// 135deg → 495deg tam bir tur (360°); 495deg görsel olarak 135deg'e eşit (mod 360), bu yüzden
// tekli parıltıda animasyon bittiğinde kart "başlangıç" görünümüne sessizce döner (sıçrama
// yok), sonsuz hover dönüşünde ise her turun sonu bir sonrakinin başıyla görsel olarak
// çakıştığı için kesintisiz görünür.
const rotateAngle = keyframes`
  from { --ha-angle: 135deg; }
  to   { --ha-angle: 495deg; }
`;

function getPrefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function getSupportsAtProperty() {
  // @property desteğinin en güvenilir JS imzası CSS.registerProperty'nin varlığıdır —
  // tarayıcılar Houdini Properties & Values API'nin bildirimsel (@property) ve emperatif
  // (CSS.registerProperty) yüzlerini birlikte gönderir. jsdom (test ortamı) hiçbirini
  // tanımlamaz; bu yüzden testlerde bu daima false döner ve animasyon hiç uygulanmaz.
  return (
    typeof window !== 'undefined' &&
    !!window.CSS &&
    typeof window.CSS.registerProperty === 'function'
  );
}

function formatValue(value, locale) {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'number' || Number.isNaN(value)) return value;
  return new Intl.NumberFormat(locale).format(value);
}

const Card = styled.div`
  @property --ha-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 135deg;
  }

  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 18px 20px;
  border: 1.5px solid transparent;
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  background:
    linear-gradient(${({ theme }) => theme.colors.surface.card}, ${({ theme }) => theme.colors.surface.card}) padding-box,
    linear-gradient(
      var(--ha-angle, 135deg),
      ${({ theme }) => theme.colors.primary.main},
      ${({ theme }) => theme.colors.secondary.main}
    ) border-box;
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  font-family: ${({ theme }) => theme.metrics.font.family};
  cursor: default;

  /* Durgun hâlde hareket yok — animasyon yalnız aşağıdaki iki koşuldan biriyle eklenir. */

  ${({ $animatable, $shimmer }) =>
    $animatable &&
    $shimmer &&
    css`
      animation: ${rotateAngle} ${SHIMMER_DURATION_MS}ms ease-out 1;
      animation-fill-mode: both;
    `}

  ${({ $animatable }) =>
    $animatable &&
    css`
      &:hover {
        animation: ${rotateAngle} ${ROTATE_DURATION_S}s linear infinite;
      }
    `}

  /* Parıltı ve hover dönüşü aynı anda tetiklenirse (nadir), kaynak sırası gereği hover
     kazanır — parıltının zamanlayıcısı zaten arka planda bitmiş olur, görsel çakışma olmaz. */

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;

    &:hover {
      animation: none !important;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary.main}, ${({ theme }) => theme.colors.secondary.main});
  color: ${({ theme }) => theme.colors.surface.card};
`;

const Label = styled.div`
  min-width: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ValueRow = styled.div`
  min-height: 38px;
  display: flex;
  align-items: center;
`;

const Value = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xxl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`;

const SuppressedText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.4;
  word-break: break-word;
`;

const BasisChip = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const BasisLabel = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const BasisValue = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

/**
 * Akıllı istatistik kartı — HR-Analytics modülünün L1 metriklerini gösterir.
 *
 * @param {string} label - İstatistik adı (ör. "Devir").
 * @param {number|null} [value] - İstatistik değeri. Bastırılmış hücrelerde null/undefined olur.
 * @param {string|null} [suppressedReason] - K-anonimlik nedeniyle bastırılmışsa backend'in
 *   döndürdüğü gerekçe kodu (ör. "ha.suppressed.belowThreshold" — HaCellSuppressor.SuppressedReasonKey
 *   sabitinin değeri; kayıtlı çeviri anahtarı `responseErrors` altındadır, `ha.*` altında DEĞİL).
 *   `responseErrors.${suppressedReason}` olarak çevrilir — bu app'teki diğer TÜM backend gerekçe
 *   kodu tüketicileriyle aynı desen (bkz. HrAnalyticsOverviewPage.js, services/api.js). Doluyken
 *   değer YERİNE çevrilmiş metin gösterilir — asla 0, asla tire, asla boş kutu.
 * @param {string[]} [basis] - Değerin dayandığı modül kodları (ör. ["EM", "OD"]) — "Temel: EM, OD"
 *   çipi. Etiket ("Temel:") zaten var olan `hrAnalytics.basis` anahtarından gelir — `ha.*` altında
 *   ayrı bir kopyası YOK (bu, sunucudan gelen kodları çeviren `ha.*` ile istemci arayüz metni olan
 *   `hrAnalytics.*` arasındaki ayrımı korur).
 */
export default function SmartStatCard({ label, value, suppressedReason, basis }) {
  const { t, i18n } = useTranslation();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);
  const [supportsAtProperty] = useState(getSupportsAtProperty);
  const [shimmer, setShimmer] = useState(false);

  // OS/tarayıcı "hareketi azalt" tercihi oturum ortasında değişirse canlı izle (CSS
  // tarafındaki @media kill-switch zaten canlıdır; bu, parıltıyı YENİDEN TETİKLEMEMEK için
  // JS tarafındaki bayrağı da güncel tutar).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setPrefersReducedMotion(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else if (mql.addListener) mql.addListener(handleChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handleChange);
      else if (mql.removeListener) mql.removeListener(handleChange);
    };
  }, []);

  // "Veri yüklendiğinde" = kart bu değeri/bastırma nedenini ilk aldığında (mount dahil) veya
  // bu ikisi güncellendiğinde (ör. panel yenilendiğinde) tek tur parıltı oynat, 1.2 sn sonra
  // durdur. Hareketi azalt açıkken hiç tetiklenmez (parıltı hiç eklenmez, testte doğrulanıyor).
  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    setShimmer(true);
    const timer = setTimeout(() => setShimmer(false), SHIMMER_DURATION_MS);
    return () => clearTimeout(timer);
  }, [value, suppressedReason, prefersReducedMotion]);

  const isSuppressed = !!suppressedReason;
  const hasBasis = Array.isArray(basis) && basis.length > 0;

  return (
    <Card $animatable={supportsAtProperty} $shimmer={shimmer} data-shimmer={shimmer ? 'true' : 'false'}>
      <Header>
        <Badge aria-hidden="true" title={t('ha.smartCard.badgeTitle')}>✦</Badge>
        <Label>{label}</Label>
      </Header>
      <ValueRow>
        {isSuppressed ? (
          <SuppressedText>
            {t(`responseErrors.${suppressedReason}`, { defaultValue: suppressedReason })}
          </SuppressedText>
        ) : (
          <Value>{formatValue(value, i18n.language)}</Value>
        )}
      </ValueRow>
      {hasBasis && (
        <BasisChip>
          <BasisLabel>{t('hrAnalytics.basis')}:</BasisLabel>{' '}
          <BasisValue>{basis.join(', ')}</BasisValue>
        </BasisChip>
      )}
    </Card>
  );
}
